import { Link } from 'react-router-dom';
import { CartItemWithProduct, LocalCartItem, Product, ProductImage, ProductVariant } from '@/types';
import { formatPrice, cn } from '@/lib/utils';

interface CartItemProps {
  item: CartItemWithProduct | (LocalCartItem & { product: Product & { images?: ProductImage[] }; variant?: ProductVariant | null });
  onUpdateQuantity: (quantity: number) => void;
  onRemove: () => void;
  disabled?: boolean;
}

export function CartItem({ item, onUpdateQuantity, onRemove, disabled = false }: CartItemProps) {
  const product = item.product;
  const variant = item.variant;
  const primaryImage = product.images?.find((img) => img.is_primary) || product.images?.[0];

  const basePrice = product.price;
  const variantAdjustment = variant?.price_adjustment || 0;
  const unitPrice = basePrice + variantAdjustment;
  const totalPrice = unitPrice * item.quantity;

  const maxQuantity = variant?.stock_quantity ?? product.stock_quantity;
  const isOutOfStock = maxQuantity === 0;

  return (
    <div
      className={cn(
        'flex gap-4 py-4 border-b border-cream-200 last:border-b-0',
        disabled && 'opacity-50 pointer-events-none'
      )}
    >
      {/* Product Image */}
      <Link
        to={`/product/${product.slug}`}
        className="flex-shrink-0 w-24 h-24 rounded-lg overflow-hidden bg-cream-100"
      >
        {primaryImage ? (
          <img
            src={primaryImage.url}
            alt={product.name}
            loading="lazy"
            decoding="async"
            className="w-full h-full object-cover hover:scale-105 transition-transform"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <svg
              className="w-8 h-8 text-cream-300"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
          </div>
        )}
      </Link>

      {/* Product Details */}
      <div className="flex-1 min-w-0">
        <Link
          to={`/product/${product.slug}`}
          className="font-display text-leather-900 hover:text-gold-700 transition-colors line-clamp-1"
        >
          {product.name}
        </Link>

        {variant && (
          <p className="text-sm text-leather-500 mt-0.5">
            {variant.option_value}
          </p>
        )}

        <p className="text-sm font-medium text-leather-700 mt-1">
          {formatPrice(unitPrice)}
        </p>

        {/* Quantity Controls */}
        <div className="flex items-center gap-3 mt-3">
          <div className="flex items-center border border-cream-300 rounded-lg">
            <button
              onClick={() => onUpdateQuantity(Math.max(1, item.quantity - 1))}
              disabled={item.quantity <= 1 || disabled}
              className="w-8 h-8 flex items-center justify-center text-leather-600 hover:bg-cream-100 disabled:opacity-50 disabled:cursor-not-allowed rounded-l-lg transition-colors"
              aria-label="Réduire la quantité"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
              </svg>
            </button>
            <span className="w-10 text-center text-sm font-medium text-leather-900">
              {item.quantity}
            </span>
            <button
              onClick={() => onUpdateQuantity(Math.min(maxQuantity, item.quantity + 1))}
              disabled={item.quantity >= maxQuantity || disabled}
              className="w-8 h-8 flex items-center justify-center text-leather-600 hover:bg-cream-100 disabled:opacity-50 disabled:cursor-not-allowed rounded-r-lg transition-colors"
              aria-label="Augmenter la quantité"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </button>
          </div>

          <button
            onClick={onRemove}
            disabled={disabled}
            className="text-leather-400 hover:text-error-600 transition-colors"
            aria-label="Supprimer du panier"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
              />
            </svg>
          </button>
        </div>

        {/* Stock Warning */}
        {isOutOfStock && (
          <p className="text-xs text-error-600 mt-2">Rupture de stock</p>
        )}
        {!isOutOfStock && maxQuantity <= 3 && (
          <p className="text-xs text-warning-600 mt-2">
            Plus que {maxQuantity} disponible{maxQuantity > 1 ? 's' : ''}
          </p>
        )}
      </div>

      {/* Total Price */}
      <div className="flex-shrink-0 text-right">
        <p className="font-semibold text-leather-900">{formatPrice(totalPrice)}</p>
      </div>
    </div>
  );
}

export default CartItem;
